package com.eladfinish.bible.contest.network.spreadsheet

import kotlinx.serialization.SerialName
import kotlinx.serialization.Serializable
import kotlinx.serialization.json.JsonPrimitive

@Serializable
data class GoogleSheetTable(
    @SerialName("version") val version: String,
    @SerialName("table") val googleSheetTableData: GoogleSheetTableData,
)

@Serializable
data class GoogleSheetTableData(
    @SerialName("cols") val cols: List<GoogleSheetColumn>,
    @SerialName("rows") val rows: List<GoogleSheetContent>,
) {

    val columns: List<GoogleSheetColumnData>
        get() = cols.map {
            val index = it.id.takeIf { id ->
                id.matches("[A-Za-z]".toRegex())
            }?.get(0)?.getAlphabetPosition() ?: -1
            GoogleSheetColumnData(index = index, label = it.label)
        }

    val rowsWithoutDummies: List<GoogleSheetContent>
        get() = rows.filterNot {
            it.list.all { googleSheetItem: GoogleSheetItem? ->
                listOf("0", "0.0", "-1", "-1.0").contains(googleSheetItem?.v?.content)
                        || googleSheetItem?.v?.content?.isEmpty() == true || googleSheetItem?.v == null
            }
        }

    // position starts at 0, i.e. a=0, b=1, c=2, ...
    private fun Char.getAlphabetPosition(): Int = lowercaseChar() - 'a'

    override fun toString(): String =
        cols.prettyPrint()
            .plus("\n")
            .plus(rows.prettyPrint())

}

@Serializable
data class GoogleSheetColumn(
    @SerialName("id") val id: String,
    @SerialName("label") val label: String,
    @SerialName("type") val type: String,
)

@Serializable
data class GoogleSheetColumnData(
    val index: Int,
    val label: String,
)

@Serializable
data class GoogleSheetContent(
    @SerialName("c") val list: List<GoogleSheetItem?>,
) {
    override fun toString(): String = list.toString()
}

@Serializable
data class GoogleSheetItem(
    @SerialName("v") val v: JsonPrimitive?,
    @SerialName("f") val f: String?,
) {
    fun getValue(): String = f ?: v?.content.orEmpty()

    fun isNull(): Boolean = v == null && f == null

    override fun toString(): String = v?.content.plus(f.orEmpty())
}

fun <T> List<T>.prettyPrint(): String =
    this.withIndex().joinToString(separator = "\n") {
        it.index.toString().plus(". ").plus(it.value)
    }

data class GoogleSheetRowHeadersAndValues(
    val list: List<Pair<GoogleSheetColumnData, String>>,
    val index: Int,
) {

    val listNoIndexes: List<Pair<String, String>>
        get() = list.map {
            it.first.label to it.second
        }

    companion object Companion {
        fun List<GoogleSheetRowHeadersAndValues>.indexOfColumnContains(label: String): Int =
            firstOrNull()?.list?.firstOrNull { pair -> pair.first.label == label }?.first?.index ?: -1

        fun List<GoogleSheetRowHeadersAndValues>.toOnlyValues(): List<List<String>> =
            map { rowHeadersAndValues ->
                rowHeadersAndValues.list.map { p: Pair<GoogleSheetColumnData, String> -> p.second }
            }
    }

}

data class GoogleSheetIndexedContent(
    val index: Int,
    val googleSheetContent: GoogleSheetContent,
)
